import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientDashboardPreferencesService } from '../services';
import api from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import {
  Layers, Sparkles, RotateCcw, ArrowLeft, Info, Loader2,
  CheckCircle2, Circle, MinusCircle, ChevronsUpDown, Users, Building2, MapPin,
  Save, X, Eye, EyeOff, Copy, AlertTriangle,
} from 'lucide-react';

const STATE_LABELS = { auto: 'Auto', on: 'Force on', off: 'Force off' };
const STATE_ICONS = {
  auto: <MinusCircle className="h-3 w-3" />,
  on:   <CheckCircle2 className="h-3 w-3" />,
  off:  <Circle className="h-3 w-3" />,
};
const LAYER_LABELS = {
  user: 'User',
  site: 'Site',
  sub_region: 'Sub-region',
  client: 'Client',
  default: 'Org default',
};

const GROUP_ORDER = ['regional', 'performers', 'kpis', 'panels', 'table', 'actions'];
const GROUP_LABELS = {
  regional:   'Regional aggregation',
  performers: 'Top / Bottom performers',
  kpis:       'KPI tiles',
  panels:     'Side panels',
  table:      'Site table',
  actions:    'Actions',
};

export default function ClientDashboardLayoutsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);

  // Single-user selection (was multi in iter319; user feedback narrowed scope)
  const [selUserId, setSelUserId] = useState(null);
  const [context, setContext] = useState(null);
  const [draft, setDraft] = useState({});
  const [savedDraftBaseline, setSavedDraftBaseline] = useState({});
  const [showNotApplicable, setShowNotApplicable] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try { setData(await clientDashboardPreferencesService.list()); }
    catch (e) { toast({ title: 'Failed to load', description: e?.message || '', variant: 'destructive' }); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-line */ }, []);

  // Fetch context for the selected user.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!selUserId) { setContext(null); setDraft({}); setSavedDraftBaseline({}); return; }
      try {
        const r = await api.get(`/client-portal/dashboard-preferences/user-context/${selUserId}`);
        if (cancelled) return;
        setContext(r.data);
        const baseline = {};
        for (const f of (data?.catalogue || [])) {
          baseline[f.key] = r.data.features.find(x => x.key === f.key)?.user_override || 'auto';
        }
        setDraft(baseline);
        setSavedDraftBaseline(baseline);
      } catch (e) {
        toast({ title: 'Failed to load user context', description: e?.message || '', variant: 'destructive' });
      }
    };
    run();
    return () => { cancelled = true; };
  }, [selUserId, data?.catalogue]);  // eslint-disable-line

  const dirty = useMemo(
    () => Object.keys(draft).some(k => draft[k] !== savedDraftBaseline[k]),
    [draft, savedDraftBaseline],
  );

  // Per-feature view combining catalogue metadata with the resolver chain
  // for the SELECTED user. (Single-user view — no merge across users.)
  const featureView = useMemo(() => {
    if (!data || !context) return [];
    return data.catalogue.map(cat => {
      const f = context.features.find(x => x.key === cat.key) || {};
      const applicable = !!(f.applicable || (f.chain || []).length > 0);
      return {
        ...cat,
        applicable,
        resolved: !!f.resolved,
        winning_layer: f.winning_layer || null,
      };
    });
  }, [data, context]);

  const setOne = (feature_key, nextState) => {
    setDraft(d => ({ ...d, [feature_key]: nextState }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const ops = Object.keys(draft).filter(k => draft[k] !== savedDraftBaseline[k]);
      if (ops.length === 0) { toast({ title: 'No changes' }); return; }
      for (const k of ops) {
        await clientDashboardPreferencesService.upsert({
          scope_type: 'user', scope_id: selUserId, feature_key: k, state: draft[k],
        });
      }
      toast({ title: 'Saved', description: `${ops.length} change${ops.length === 1 ? '' : 's'} applied.` });
      const r = await api.get(`/client-portal/dashboard-preferences/user-context/${selUserId}`);
      setContext(r.data);
      setSavedDraftBaseline(draft);
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || e?.message || '', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const handleCancel = () => {
    setDraft(savedDraftBaseline);
    toast({ title: 'Changes discarded' });
  };

  const resetUserOverrides = async () => {
    if (!selUserId) return;
    setSaving(true);
    try {
      await clientDashboardPreferencesService.resetScope('user', selUserId);
      toast({ title: 'User overrides cleared' });
      const r = await api.get(`/client-portal/dashboard-preferences/user-context/${selUserId}`);
      setContext(r.data);
      const baseline = {}; for (const f of data.catalogue) baseline[f.key] = 'auto';
      setDraft(baseline); setSavedDraftBaseline(baseline);
    } catch (e) {
      toast({ title: 'Reset failed', description: e?.message || '', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading || !data) {
    return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;
  }

  const groups = {};
  for (const f of featureView) (groups[f.group] = groups[f.group] || []).push(f);

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="dashboard-layouts-page">
      <Toaster />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/settings')} data-testid="back-to-settings">
            <ArrowLeft className="h-4 w-4 mr-1" /> Settings
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><Layers className="h-6 w-6 text-violet-600" /> Dashboard Layouts</h1>
            <p className="text-sm text-muted-foreground">Override which dashboard features render for a specific client portal user.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleCancel} disabled={!dirty || saving} data-testid="cancel-btn"><X className="h-4 w-4 mr-1" /> Cancel</Button>
          <Button onClick={handleSave} disabled={!dirty || saving || !selUserId} data-testid="save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}Save changes
          </Button>
        </div>
      </div>

      {/* User picker (single-select) */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4 text-emerald-600" /> Client User</CardTitle>
              <p className="text-xs text-muted-foreground">Select a single client portal user to configure their dashboard. Their group / client / site / region context is inherited automatically and shown below.</p>
            </div>
            {selUserId && (
              <Dialog open={copyOpen} onOpenChange={setCopyOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="copy-access-btn">
                    <Copy className="h-3.5 w-3.5 mr-1" /> Copy this user&apos;s dashboard to others…
                  </Button>
                </DialogTrigger>
                <CopyAccessDialog
                  source={data.client_users.find(u => u.id === selUserId)}
                  others={data.client_users.filter(u => u.id !== selUserId)}
                  onClose={(refreshAfter) => {
                    setCopyOpen(false);
                    if (refreshAfter) refresh();
                  }}
                />
              </Dialog>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <SingleUserPicker users={data.client_users} value={selUserId} onChange={setSelUserId} />
        </CardContent>
      </Card>

      {/* Context card */}
      {context && (
        <Card data-testid="context-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><Info className="h-4 w-4 text-blue-600" /> What this user can see</CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <ContextField icon={<Building2 className="h-3.5 w-3.5" />} label="Group(s)"
                value={context.context.assigned_groups.length === 0 ? '—' : context.context.assigned_groups.map(g => g.name).join(', ')}
                testId="ctx-groups" />
              <ContextField icon={<Layers className="h-3.5 w-3.5" />} label="Sub-region(s)"
                value={context.context.assigned_sub_regions.length === 0 ? '—' : context.context.assigned_sub_regions.map(r => r.label).join(', ')}
                testId="ctx-subregions" />
              <ContextField icon={<MapPin className="h-3.5 w-3.5" />} label="Effective site count"
                value={String(context.context.effective_site_count)}
                testId="ctx-sitecount" />
              <ContextField icon={<Sparkles className="h-3.5 w-3.5" />} label="Layout tier"
                value={
                  context.context.effective_site_count === 0 ? 'No sites assigned'
                  : context.context.effective_site_count === 1 ? 'Single Site'
                  : !context.context.has_sub_regions ? 'Small Multi-Site'
                  : context.context.is_large_portfolio ? 'Large Group' : 'Small + Regional'
                }
                testId="ctx-layout" />
            </div>
            {context.context.assigned_sites.length > 0 && (
              <details className="mt-3 text-xs text-slate-600">
                <summary className="cursor-pointer hover:text-slate-900">Assigned sites ({context.context.assigned_sites.length})</summary>
                <div className="mt-2 max-h-40 overflow-auto border rounded p-2 bg-slate-50">
                  {context.context.assigned_sites.map(s => (
                    <div key={s.id} className="text-xs py-0.5 flex justify-between"><span>{s.name}</span><span className="text-slate-400">{s.client_name || ''}</span></div>
                  ))}
                </div>
              </details>
            )}
          </CardContent>
        </Card>
      )}

      {/* Feature matrix */}
      {!selUserId ? (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">Select a client user above to configure their dashboard.</CardContent></Card>
      ) : (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-base">Feature controls</CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setShowNotApplicable(!showNotApplicable)} data-testid="toggle-not-applicable">
                  {showNotApplicable ? <EyeOff className="h-3.5 w-3.5 mr-1" /> : <Eye className="h-3.5 w-3.5 mr-1" />}
                  {showNotApplicable ? 'Hide' : 'Show'} not-applicable rows
                </Button>
                <Button variant="outline" size="sm" onClick={resetUserOverrides} disabled={saving} data-testid="reset-user-btn">
                  <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset user overrides
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {GROUP_ORDER.filter(g => groups[g]?.length).map(g => {
              const visibleRows = groups[g].filter(f => showNotApplicable || f.applicable);
              if (visibleRows.length === 0) return null;
              return (
                <div key={g} className="mb-6 last:mb-0">
                  <h3 className="text-sm font-semibold text-slate-800 mb-2">{GROUP_LABELS[g]}</h3>
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left p-3 font-medium text-slate-600">Feature</th>
                          <th className="text-left p-3 font-medium text-slate-600 w-[230px]">Currently</th>
                          <th className="text-left p-3 font-medium text-slate-600 w-[300px]">User override</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleRows.map(f => <FeatureRow key={f.key} f={f} draft={draft} setOne={setOne} />)}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

const ContextField = ({ icon, label, value, testId }) => (
  <div className="border border-slate-200 rounded-md p-3 bg-slate-50">
    <p className="text-[11px] uppercase tracking-wide text-slate-500 flex items-center gap-1 mb-1">{icon}{label}</p>
    <p className="text-sm font-medium text-slate-900" data-testid={testId}>{value}</p>
  </div>
);

const FeatureRow = ({ f, draft, setOne }) => {
  const draftState = draft[f.key] || 'auto';
  let currentBadge;
  if (!f.applicable) {
    currentBadge = <Badge variant="outline" className="bg-slate-100 text-slate-600 border-0 text-[11px]">Not applicable</Badge>;
  } else if (f.resolved) {
    currentBadge = <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-0 text-[11px]">Currently shown</Badge>;
  } else {
    currentBadge = <Badge variant="outline" className="bg-rose-50 text-rose-800 border-0 text-[11px]">Currently hidden</Badge>;
  }
  const layerLabel = f.winning_layer
    ? `from ${LAYER_LABELS[f.winning_layer]} override`
    : 'from auto-gate';
  return (
    <tr className="border-t border-slate-100" data-testid={`feature-row-${f.key}`}>
      <td className="p-3">
        <p className="font-medium">{f.label}</p>
        {!f.applicable && <p className="text-[10px] text-slate-500 mt-0.5">Auto-gate: <span className="font-medium">{f.auto_gate}</span> — wouldn&apos;t fire for this user</p>}
      </td>
      <td className="p-3" data-testid={`currently-${f.key}`}>
        {currentBadge}
        <p className="text-[10px] text-slate-500 mt-1">{layerLabel}</p>
      </td>
      <td className="p-3">
        <Tabs value={draftState} onValueChange={(v) => setOne(f.key, v)}>
          <TabsList className="grid grid-cols-3 h-8">
            {['auto','on','off'].map(s => (
              <TabsTrigger key={s} value={s} className="text-xs flex items-center gap-1" data-testid={`feature-${f.key}-state-${s}`}>
                {STATE_ICONS[s]} {STATE_LABELS[s]}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </td>
    </tr>
  );
};

const SingleUserPicker = ({ users, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return users;
    return users.filter(u => u.name.toLowerCase().includes(t) || u.email.toLowerCase().includes(t));
  }, [users, q]);
  const selected = users.find(u => u.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid="scope-user-picker" disabled={users.length === 0}
          className="w-full md:w-[480px] inline-flex items-center justify-between rounded-md border border-slate-200 bg-white px-3 h-10 text-sm disabled:opacity-50 hover:border-slate-300">
          <span className={`truncate ${selected ? 'text-slate-900 font-medium' : 'text-slate-400'}`}>
            {users.length === 0 ? '— No client users —' : (selected ? selected.name : '— Select user —')}
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 ml-2 text-slate-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-2 w-[420px]" align="start">
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search users..." className="h-8 mb-2" data-testid="scope-user-search" />
        <div className="max-h-72 overflow-auto">
          {filtered.length === 0 && <p className="text-xs text-muted-foreground p-2">No matches.</p>}
          {filtered.map(u => (
            <button key={u.id} type="button"
              onClick={() => { onChange(u.id); setOpen(false); }}
              className={`w-full flex items-center gap-2 p-1.5 rounded text-left ${value === u.id ? 'bg-emerald-50' : 'hover:bg-slate-50'}`}
              data-testid={`scope-user-option-${u.id}`}>
              <input type="radio" checked={value === u.id} readOnly className="h-3.5 w-3.5" />
              <div className="min-w-0">
                <p className="text-sm truncate">{u.name}</p>
                <p className="text-[10px] text-slate-500 truncate">{u.email}</p>
              </div>
            </button>
          ))}
        </div>
        {value && (
          <div className="flex justify-end mt-2 pt-2 border-t border-slate-100">
            <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => { onChange(null); setQ(''); }} data-testid="scope-user-clear">Clear</Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

const CopyAccessDialog = ({ source, others, onClose }) => {
  const { toast } = useToast();
  const [targets, setTargets] = useState([]);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return others;
    return others.filter(u => u.name.toLowerCase().includes(t) || u.email.toLowerCase().includes(t));
  }, [others, q]);
  const toggle = (id) => setTargets(t => t.includes(id) ? t.filter(x => x !== id) : [...t, id]);
  const handleCopy = async () => {
    if (!source || targets.length === 0) return;
    setBusy(true);
    try {
      const r = await clientDashboardPreferencesService.copyUserOverrides(source.id, targets);
      toast({
        title: 'Dashboard configuration copied',
        description: `${r.total_overrides_copied} override${r.total_overrides_copied === 1 ? '' : 's'} applied to ${r.targets.length} user${r.targets.length === 1 ? '' : 's'}.`,
      });
      onClose(true);
    } catch (e) {
      toast({ title: 'Copy failed', description: e?.response?.data?.detail || e?.message || '', variant: 'destructive' });
    } finally { setBusy(false); }
  };
  return (
    <DialogContent className="max-w-2xl" data-testid="copy-access-dialog">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2"><Copy className="h-4 w-4 text-violet-600" /> Copy dashboard configuration</DialogTitle>
        <DialogDescription>
          Copy <span className="font-medium text-slate-900">{source?.name}</span>&apos;s dashboard layout overrides onto one or more other client users.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-3 my-2">
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 flex gap-2 items-start" data-testid="copy-overwrite-warning">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-900">
            <p className="font-semibold">Existing dashboard configuration will be overwritten</p>
            <p className="text-xs mt-0.5">Each selected target user&apos;s current user-level overrides will be wiped and replaced with {source?.name}&apos;s.</p>
          </div>
        </div>
        <div className="rounded-md border border-blue-200 bg-blue-50 p-3 flex gap-2 items-start" data-testid="copy-scope-notice">
          <Info className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
          <div className="text-sm text-blue-900">
            <p className="font-semibold">Only the dashboard layout is copied</p>
            <p className="text-xs mt-0.5">Site, group, and sub-region assignments are <span className="font-medium">NOT</span> copied. Each user keeps their own access scope — only the format/visibility of dashboard widgets is duplicated.</p>
          </div>
        </div>
      </div>

      <div>
        <p className="text-sm font-medium mb-2">Copy to:</p>
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search users..." className="h-8 mb-2" data-testid="copy-target-search" />
        <div className="max-h-72 overflow-auto border border-slate-200 rounded-md p-2">
          {filtered.length === 0 && <p className="text-xs text-muted-foreground p-2">No other client users.</p>}
          {filtered.map(u => {
            const checked = targets.includes(u.id);
            return (
              <button key={u.id} type="button" onClick={() => toggle(u.id)}
                className="w-full flex items-center gap-2 p-1.5 rounded hover:bg-slate-50 text-left"
                data-testid={`copy-target-${u.id}`}>
                <input type="checkbox" checked={checked} readOnly className="h-3.5 w-3.5" />
                <div className="min-w-0">
                  <p className="text-sm truncate">{u.name}</p>
                  <p className="text-[10px] text-slate-500 truncate">{u.email}</p>
                </div>
              </button>
            );
          })}
        </div>
        {targets.length > 0 && (
          <p className="text-xs text-slate-600 mt-2"><span className="font-medium">{targets.length}</span> target{targets.length === 1 ? '' : 's'} selected</p>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={() => onClose(false)} data-testid="copy-cancel-btn">Cancel</Button>
        <Button onClick={handleCopy} disabled={busy || targets.length === 0} data-testid="copy-confirm-btn">
          {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Copy className="h-4 w-4 mr-1" />}Copy &amp; overwrite {targets.length > 0 ? `(${targets.length})` : ''}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};
